import { useState, useCallback } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';

/**
 * Adventure data from the backend.
 */
interface Adventure {
    id: number;
    name: string;
    slug: string;
    description: string | null;
    difficulty_modifier: number;
    is_active: boolean;
    challenges_count: number;
    rewards_count: number;
    attempts_count: number;
    created_by: string | null;
    created_at: string;
}

/**
 * Pagination link from Laravel.
 */
interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

/**
 * Paginated adventures response.
 */
interface PaginatedAdventures {
    data: Adventure[];
    links: PaginationLink[];
    last_page: number;
}

/**
 * Props for the Adventures Index page.
 */
interface Props {
    adventures: PaginatedAdventures;
    filters: {
        search: string | null;
        active: string | null;
    };
}

/**
 * GM Adventures Index page - displays all adventures with management actions.
 */
export default function Index({ adventures, filters }: Props) {
    const [search, setSearch] = useState(filters.search || '');
    const [activeFilter, setActiveFilter] = useState(filters.active || '');

    // Handle search/filter submission
    const handleFilter = useCallback(() => {
        router.get('/gm/adventures', {
            search: search || undefined,
            active: activeFilter || undefined,
        }, { preserveState: true });
    }, [search, activeFilter]);

    // Clear all filters
    const handleClearFilters = () => {
        setSearch('');
        setActiveFilter('');
        router.get('/gm/adventures', {}, { preserveState: true });
    };

    // Handle delete
    const handleDelete = (adventure: Adventure) => {
        if (confirm(`Are you sure you want to delete "${adventure.name}"? This will also delete all ${adventure.attempts_count} attempts.`)) {
            router.delete(`/gm/adventures/${adventure.id}`, { preserveScroll: true });
        }
    };

    return (
        <GmLayout title="Adventures" activePath="/gm/adventures">
            <div className="max-w-7xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">Adventures</h1>
                    <Link
                        href="/gm/adventures/create"
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                    >
                        + Create Adventure
                    </Link>
                </div>

                {/* Search and Filters */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 mb-6">
                    <div className="flex flex-wrap gap-4 items-end">
                        {/* Search Input */}
                        <div className="flex-1 min-w-[200px]">
                            <label className="block text-sm text-gray-400 mb-1">Search</label>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
                                placeholder="Adventure name or description"
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400"
                            />
                        </div>

                        {/* Active Filter */}
                        <div className="w-40">
                            <label className="block text-sm text-gray-400 mb-1">Status</label>
                            <select
                                value={activeFilter}
                                onChange={(e) => setActiveFilter(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            >
                                <option value="">All</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>

                        {/* Filter Buttons */}
                        <div className="flex gap-2">
                            <button
                                onClick={handleFilter}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                            >
                                Apply
                            </button>
                            <button
                                onClick={handleClearFilters}
                                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Adventures Table */}
                {adventures.data.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        No adventures found.
                    </div>
                ) : (
                    <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Name</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Difficulty</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Challenges</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Rewards</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Attempts</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {adventures.data.map((adventure) => (
                                        <AdventureRow key={adventure.id} adventure={adventure} onDelete={handleDelete} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Pagination */}
                {adventures.last_page > 1 && (
                    <Pagination links={adventures.links} />
                )}
            </div>
        </GmLayout>
    );
}

/**
 * AdventureRow - Displays a single adventure row with actions.
 */
function AdventureRow({ adventure, onDelete }: { adventure: Adventure; onDelete: (adventure: Adventure) => void }) {
    return (
        <tr>
            {/* Name */}
            <td className="px-4 py-3">
                <div>
                    <Link href={`/gm/adventures/${adventure.id}`} className="text-white font-medium hover:text-purple-400">
                        {adventure.name}
                    </Link>
                    {adventure.description && (
                        <p className="text-xs text-gray-400 truncate max-w-xs">{adventure.description}</p>
                    )}
                </div>
            </td>

            {/* Status */}
            <td className="px-4 py-3">
                <span className={`px-2 py-1 text-xs rounded ${adventure.is_active ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}>
                    {adventure.is_active ? 'Active' : 'Inactive'}
                </span>
            </td>

            {/* Difficulty */}
            <td className="px-4 py-3 text-sm text-gray-300">
                {adventure.difficulty_modifier > 0 ? '+' : ''}{adventure.difficulty_modifier}
            </td>

            {/* Challenges */}
            <td className="px-4 py-3 text-sm text-gray-400">
                {adventure.challenges_count}
            </td>

            {/* Rewards */}
            <td className="px-4 py-3 text-sm text-gray-400">
                {adventure.rewards_count}
            </td>

            {/* Attempts */}
            <td className="px-4 py-3 text-sm text-gray-400">
                {adventure.attempts_count}
            </td>

            {/* Actions */}
            <td className="px-4 py-3">
                <div className="flex gap-2">
                    <Link
                        href={`/gm/adventures/${adventure.id}`}
                        className="px-3 py-1 text-xs bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                    >
                        View
                    </Link>
                    <Link
                        href={`/gm/adventures/${adventure.id}/edit`}
                        className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    >
                        Edit
                    </Link>
                    <button
                        onClick={() => onDelete(adventure)}
                        className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                    >
                        Delete
                    </button>
                </div>
            </td>
        </tr>
    );
}

/**
 * Pagination - Displays pagination links.
 */
function Pagination({ links }: { links: PaginationLink[] }) {
    return (
        <div className="mt-6 flex justify-center gap-1">
            {links.map((link, index) => (
                <a
                    key={index}
                    href={link.url || '#'}
                    className={`px-3 py-1 rounded text-sm ${
                        link.active
                            ? 'bg-purple-600 text-white'
                            : link.url
                            ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            : 'bg-slate-800 text-gray-500 cursor-not-allowed'
                    }`}
                    dangerouslySetInnerHTML={{ __html: link.label }}
                />
            ))}
        </div>
    );
}

